import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { useSnackbar } from 'notistack';
import { useAppContext } from './AppContext';
import { getAccessToken } from '../Shared/Api';
import {
	buildWebSocketQuery,
	getWebSocketUrl,
	REALTIME_EVENT_LABELS,
} from '../Shared/realtime';

const RealtimeContext = createContext(null);

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export const RealtimeProvider = ({ children }) => {
	const { state } = useAppContext();
	const { enqueueSnackbar } = useSnackbar();
	const listenersRef = useRef(new Set());
	const wsRef = useRef(null);
	const reconnectAttemptRef = useRef(0);
	const reconnectTimerRef = useRef(null);

	const subscribe = useCallback((listener) => {
		listenersRef.current.add(listener);
		return () => listenersRef.current.delete(listener);
	}, []);

	const notifyListeners = useCallback((message) => {
		listenersRef.current.forEach((listener) => {
			try {
				listener(message);
			} catch (error) {
				console.error('Realtime listener error:', error);
			}
		});
	}, []);

	const showNotification = useCallback(
		(message) => {
			if (message?.type === 'bid.updated') {
				const label = REALTIME_EVENT_LABELS[message.event];
				if (label) {
					enqueueSnackbar(label, { variant: 'info' });
				}
				return;
			}
			if (message?.type === 'job.email_event' && message.event === 'opened') {
				const label = REALTIME_EVENT_LABELS.opened;
				if (label) {
					enqueueSnackbar(label, { variant: 'info' });
				}
			}
		},
		[enqueueSnackbar],
	);

	useEffect(() => {
		if (!state.initialized || !state.isAuthenticated || !state.accessToken) {
			return undefined;
		}

		let cancelled = false;

		const clearReconnectTimer = () => {
			if (reconnectTimerRef.current) {
				clearTimeout(reconnectTimerRef.current);
				reconnectTimerRef.current = null;
			}
		};

		const scheduleReconnect = () => {
			if (cancelled) {
				return;
			}
			const delay = Math.min(
				RECONNECT_BASE_MS * 2 ** reconnectAttemptRef.current,
				RECONNECT_MAX_MS,
			);
			reconnectAttemptRef.current += 1;
			reconnectTimerRef.current = setTimeout(connect, delay);
		};

		const connect = () => {
			if (cancelled) {
				return;
			}

			clearReconnectTimer();

			// Read fresh token each attempt — HTTP interceptors refresh in localStorage
			// without updating AppContext, so state.accessToken can be stale.
			const accessToken = getAccessToken();
			if (!accessToken) {
				scheduleReconnect();
				return;
			}

			const query = buildWebSocketQuery(accessToken);
			const ws = new WebSocket(`${getWebSocketUrl()}?${query}`);
			wsRef.current = ws;

			ws.onopen = () => {
				reconnectAttemptRef.current = 0;
			};

			ws.onmessage = (event) => {
				try {
					const message = JSON.parse(event.data);
					showNotification(message);
					notifyListeners(message);
				} catch (error) {
					console.error('Failed to parse realtime message:', error);
				}
			};

			ws.onclose = () => {
				if (!cancelled) {
					scheduleReconnect();
				}
			};

			ws.onerror = () => {
				ws.close();
			};
		};

		connect();

		return () => {
			cancelled = true;
			clearReconnectTimer();
			if (wsRef.current) {
				wsRef.current.close();
				wsRef.current = null;
			}
		};
	}, [
		state.initialized,
		state.isAuthenticated,
		state.accessToken,
		state.user?.organization?.id,
		notifyListeners,
		showNotification,
	]);

	const value = useMemo(() => ({ subscribe }), [subscribe]);

	return (
		<RealtimeContext.Provider value={value}>
			{children}
		</RealtimeContext.Provider>
	);
};

export const useRealtime = () => {
	const context = useContext(RealtimeContext);
	if (!context) {
		throw new Error('useRealtime must be used within RealtimeProvider');
	}
	return context;
};

export const useRealtimeEvent = (handler) => {
	const { subscribe } = useRealtime();

	useEffect(() => {
		if (!handler) {
			return undefined;
		}
		return subscribe(handler);
	}, [handler, subscribe]);
};
